/**
 * RequestMoreCreditsModal — the member "ask an admin for more credits" flow.
 *
 * A NEW sibling to {@link CreditGuardHost} (never edits it): a small modal that
 * lets a member optionally name an amount + a short note, then calls
 * `POST /api/v1/credits/request-more` (lib/api/userBudgets.ts). CreditGuardHost
 * keeps its own one-click "Demander à un admin" shortcut inside the blocking
 * 402 dialog; this component is the richer, deliberate version — mounted from
 * the Credits settings page and from {@link OutOfCreditsRequestMoreHost} (a
 * separate global host that reacts to the SAME insufficient-credits event
 * CreditGuardHost listens to, without touching that file).
 *
 * Exports:
 *   - {@link RequestMoreCreditsModal}: controlled modal (isOpen/onClose).
 *   - {@link RequestMoreCreditsButton}: self-contained trigger + modal, drop
 *     this anywhere (e.g. the Credits settings page).
 *   - {@link useRequestMoreCreditsPrompt}: hook exposing the latest
 *     insufficient-credits event so a host component can react to it.
 *   - {@link OutOfCreditsRequestMoreHost}: mountable global host (sibling of
 *     CreditGuardHost) that surfaces a "make a detailed request" affordance
 *     right after an out-of-credits event, WITHOUT editing CreditGuardHost.
 */
import { useEffect, useRef, useState } from 'react';

import {
  AI_CREDITS_INSUFFICIENT_EVENT,
  type NormalizedInsufficientCredits,
} from '@/lib/api/ai-credits';
import { requestMoreCredits } from '@/lib/api/userBudgets';
import { useT } from '@/i18n';
import { Modal, useToast } from '@/ui';
import { Button, Field, TextArea, TextInput } from '@/routes/settings/ui';
import { featureLabel } from './featureLabels';

const COPY = {
  title: { fr: 'Demander plus de crédits IA', en: 'Request more AI credits' },
  intro: {
    fr: 'Envoyez une demande aux administrateurs de votre organisation pour augmenter votre plafond ou recharger le portefeuille commun.',
    en: 'Send a request to your organization’s admins to raise your cap or top up the shared wallet.',
  },
  amountLabel: { fr: 'Crédits souhaités (optionnel)', en: 'Credits requested (optional)' },
  amountHint: {
    fr: 'Laissez vide si vous ne connaissez pas le montant exact.',
    en: 'Leave blank if you do not know the exact amount.',
  },
  noteLabel: { fr: 'Message (optionnel)', en: 'Message (optional)' },
  notePlaceholder: {
    fr: 'Ex. : je vais dépasser mon plafond cette semaine pour la campagne X…',
    en: 'e.g. I’m about to hit my cap this week for campaign X…',
  },
  cancel: { fr: 'Annuler', en: 'Cancel' },
  send: { fr: 'Envoyer la demande', en: 'Send request' },
  sending: { fr: 'Envoi…', en: 'Sending…' },
  sent: { fr: 'Demande envoyée à un administrateur.', en: 'Request sent to an admin.' },
  failed: {
    fr: "Impossible d'envoyer la demande pour le moment.",
    en: 'Could not send the request right now.',
  },
  triggerLabel: { fr: 'Demander plus de crédits', en: 'Request more credits' },
  promptTitle: { fr: 'Encore besoin de crédits ?', en: 'Still need more credits?' },
  promptBody: {
    fr: 'Faites une demande détaillée à un administrateur avec le montant et le contexte.',
    en: 'Send an admin a detailed request with the amount and context.',
  },
  promptCta: { fr: 'Faire une demande', en: 'Make a request' },
  promptDismiss: { fr: 'Ignorer', en: 'Dismiss' },
} as const;

type Locale = 'fr' | 'en';

export interface RequestMoreCreditsModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Prefilled amount (e.g. the missing credits from a 402), editable. */
  defaultAmount?: number | null;
  /** Prefilled note (e.g. the blocked feature's friendly label), editable. */
  defaultNote?: string;
  onSent?: () => void;
}

export function RequestMoreCreditsModal({
  isOpen,
  onClose,
  defaultAmount = null,
  defaultNote = '',
  onSent,
}: RequestMoreCreditsModalProps) {
  const { locale } = useT();
  const L: Locale = locale === 'en' ? 'en' : 'fr';
  const toast = useToast();
  const [amount, setAmount] = useState(defaultAmount != null && defaultAmount > 0 ? String(Math.ceil(defaultAmount)) : '');
  const [note, setNote] = useState(defaultNote);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Re-seed the form each time the modal is (re)opened with new context.
  useEffect(() => {
    if (isOpen) {
      setAmount(defaultAmount != null && defaultAmount > 0 ? String(Math.ceil(defaultAmount)) : '');
      setNote(defaultNote);
      setError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  if (!isOpen) return null;

  const trimmedAmount = amount.trim();
  const parsedAmount = trimmedAmount === '' ? undefined : Number(trimmedAmount);
  const amountInvalid = parsedAmount !== undefined && (!Number.isFinite(parsedAmount) || parsedAmount < 0);

  const submit = async () => {
    if (amountInvalid || busy) return;
    setBusy(true);
    setError(null);
    try {
      await requestMoreCredits({
        note: note.trim() || undefined,
        amount: parsedAmount,
      });
      toast.success(COPY.sent[L], 5000);
      onSent?.();
      onClose();
    } catch {
      setError(COPY.failed[L]);
      toast.error(COPY.failed[L], 5000);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title={COPY.title[L]}
      size="md"
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            {COPY.cancel[L]}
          </Button>
          <Button variant="primary" onClick={() => void submit()} disabled={busy || amountInvalid}>
            {busy ? COPY.sending[L] : COPY.send[L]}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <p className="text-sm text-muted">{COPY.intro[L]}</p>
        <Field label={COPY.amountLabel[L]} hint={COPY.amountHint[L]}>
          <TextInput
            type="number"
            min={0}
            step="1"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="500"
            aria-invalid={amountInvalid ? true : undefined}
          />
        </Field>
        <Field label={COPY.noteLabel[L]}>
          <TextArea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder={COPY.notePlaceholder[L]}
            rows={3}
          />
        </Field>
        {error && <p className="text-xs text-danger">{error}</p>}
      </div>
    </Modal>
  );
}

export interface RequestMoreCreditsButtonProps {
  label?: string;
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger';
  className?: string;
  defaultAmount?: number | null;
  defaultNote?: string;
}

/** Self-contained trigger + modal — drop anywhere (e.g. the settings page). */
export function RequestMoreCreditsButton({
  label,
  variant = 'secondary',
  className,
  defaultAmount = null,
  defaultNote = '',
}: RequestMoreCreditsButtonProps) {
  const { locale } = useT();
  const L: Locale = locale === 'en' ? 'en' : 'fr';
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button variant={variant} className={className} onClick={() => setOpen(true)}>
        {label ?? COPY.triggerLabel[L]}
      </Button>
      <RequestMoreCreditsModal
        isOpen={open}
        onClose={() => setOpen(false)}
        defaultAmount={defaultAmount}
        defaultNote={defaultNote}
      />
    </>
  );
}

// --- Global "out of credits" follow-up prompt --------------------------------
//
// Listens to the SAME `psn:ai-credits-insufficient` window event CreditGuardHost
// reacts to, entirely independently (no import of/edit to that file). Exposed
// both as a hook (for a page that wants the raw state) and as a ready-to-mount
// host component (a sibling to <CreditGuardHost/> in the shell).

export interface RequestMoreCreditsPromptState {
  pending: NormalizedInsufficientCredits | null;
  dismiss: () => void;
}

/** Tracks the latest insufficient-credits event for a "request more" follow-up UI. */
export function useRequestMoreCreditsPrompt(): RequestMoreCreditsPromptState {
  const [pending, setPending] = useState<NormalizedInsufficientCredits | null>(null);

  useEffect(() => {
    const onInsufficient = (e: Event) => {
      const detail = (e as CustomEvent<NormalizedInsufficientCredits>).detail ?? {};
      setPending(detail);
    };
    window.addEventListener(AI_CREDITS_INSUFFICIENT_EVENT, onInsufficient);
    return () => window.removeEventListener(AI_CREDITS_INSUFFICIENT_EVENT, onInsufficient);
  }, []);

  return { pending, dismiss: () => setPending(null) };
}

/**
 * OutOfCreditsRequestMoreHost — mount once in the shell, alongside (NOT inside)
 * `<CreditGuardHost/>`. A few seconds after an insufficient-credits event, shows
 * a small dismissible corner card offering the detailed request-more modal,
 * prefilled with the missing amount + blocked feature. Independent lifecycle
 * from CreditGuardHost's own blocking dialog — closing one does not affect the
 * other, and this host never renders anything until an out-of-credits event
 * has actually fired in this session.
 */
export function OutOfCreditsRequestMoreHost() {
  const { locale } = useT();
  const L: Locale = locale === 'en' ? 'en' : 'fr';
  const { pending, dismiss } = useRequestMoreCreditsPrompt();
  const [visible, setVisible] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const timerRef = useRef<number | null>(null);

  useEffect(() => {
    if (!pending) {
      setVisible(false);
      return;
    }
    // Let CreditGuardHost's own modal appear first; this is a soft follow-up,
    // not a competing dialog.
    timerRef.current = window.setTimeout(() => setVisible(true), 1200);
    return () => {
      if (timerRef.current != null) window.clearTimeout(timerRef.current);
    };
  }, [pending]);

  // The corner card hides itself as soon as « Faire une demande » is clicked, but the modal it opens
  // lives in this same tree: returning null while the card was hidden unmounted the modal before it
  // could render, so the request form never appeared. Keep rendering while the modal is open.
  if (!pending && !modalOpen) return null;
  if (!modalOpen && !visible) return null;

  const missing =
    pending && pending.required != null && pending.available != null
      ? Math.max(pending.required - pending.available, 0)
      : null;
  const note = pending?.feature ? featureLabel(pending.feature) : undefined;

  const close = () => {
    setVisible(false);
    dismiss();
  };

  return (
    <>
      {visible && !modalOpen && (
      <div
        role="status"
        className="fixed bottom-4 right-4 z-40 w-[calc(100vw-2rem)] max-w-sm rounded-token-lg border border-border bg-surface p-3 shadow-elevation-2 sm:right-6 sm:bottom-6"
      >
        <p className="text-sm font-semibold text-foreground">{COPY.promptTitle[L]}</p>
        <p className="mt-1 text-xs text-muted">{COPY.promptBody[L]}</p>
        <div className="mt-3 flex justify-end gap-2">
          <Button variant="ghost" onClick={close}>
            {COPY.promptDismiss[L]}
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              setModalOpen(true);
              setVisible(false);
            }}
          >
            {COPY.promptCta[L]}
          </Button>
        </div>
      </div>
      )}
      <RequestMoreCreditsModal
        isOpen={modalOpen}
        onClose={() => {
          setModalOpen(false);
          dismiss();
        }}
        defaultAmount={missing}
        defaultNote={note}
      />
    </>
  );
}
